import { isEmpty, map } from "lodash";
import React from "react";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import useSharedStyles from "../../employer/Agencies/ActiveAgencies/ActiveAgenciesTable/useSharedStyles";
import GlobalAvatar from "../../General/GlobalAvatar";
import PopoverLink from "../../General/PopoverLink";
import { formatter } from "../../recruiter/Candidates/actions/modals/utils";
import { RecruiterSubmissionName } from "../Contracts/tableUtils";
import {
  SHORT_DAY_HEADERS,
  WEEKEND_DAYS,
  WEEK_DAYS,
  orderedDays,
} from "../../../util/workWeek";

const cellStyle = { width: "48px", padding: "8px" };
const boldCellStyle = { ...cellStyle, fontWeight: "bold" };
const sectionHeaderStyle = {
  padding: "6px 8px",
  fontWeight: "bold",
  fontSize: "12px",
  color: "#008DAE",
  textAlign: "left",
  borderTop: "1px solid #e0e0e0",
};

const hasAllocations = (allocations) =>
  allocations &&
  typeof allocations === "object" &&
  Object.keys(allocations).length > 0;

// Daily hours are stored Monday-first; the columns are rendered starting on the first day
// of the work period's cycle. Saturday and Sunday stay hidden until someone works them.
const WEEKEND_INDICES = WEEKEND_DAYS.map((day) => WEEK_DAYS.indexOf(day));

const getDayLayout = (details, initialWeekday) => {
  const hasWeekend = details.some((row) =>
    WEEKEND_INDICES.some((i) => Number((row || [])[i] ?? 0) > 0)
  );
  const visibleDays = hasWeekend
    ? orderedDays(initialWeekday)
    : orderedDays(initialWeekday).filter((day) => !WEEKEND_DAYS.includes(day));

  return {
    headers: visibleDays.map((day) => SHORT_DAY_HEADERS[day]),
    dayIndices: visibleDays.map((day) => WEEK_DAYS.indexOf(day)),
  };
};

// Empty in, empty out — an absent breakdown must keep rendering no cells at all.
const orderDays = (row, dayIndices) =>
  row && row.length > 0 ? dayIndices.map((i) => row[i]) : [];

const popoverTable = ({
  standardHoursDetails,
  nonStandardHoursDetails,
  doubleTimeHoursDetails,
  workPeriodInitialWeekday,
}) => {
  const { headers, dayIndices } = getDayLayout(
    [standardHoursDetails, nonStandardHoursDetails, doubleTimeHoursDetails],
    workPeriodInitialWeekday
  );

  const standardRow = orderDays(standardHoursDetails, dayIndices);
  const nonStandardRow = orderDays(nonStandardHoursDetails, dayIndices);
  const doubleTimeRow = orderDays(doubleTimeHoursDetails, dayIndices);

  return (
    <table
      className="rounded"
      style={{ textAlign: "center" }}
      data-cy="timesheet-hours-popover"
    >
      <tbody>
        <tr>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <th />
          {headers.map((day) => (
            <th key={day}>{day}</th>
          ))}
        </tr>
        <tr>
          <td style={boldCellStyle}>Std.</td>
          {standardRow.map((hours, i) => (
            <td key={i} style={cellStyle}>
              {hours}
            </td>
          ))}
        </tr>
        {nonStandardRow.length > 0 && (
          <tr>
            <td style={boldCellStyle}>1.5x</td>
            {nonStandardRow.map((hours, i) => (
              <td key={i} style={cellStyle}>
                {hours}
              </td>
            ))}
          </tr>
        )}
        {doubleTimeRow.length > 0 && (
          <tr>
            <td style={boldCellStyle}>2x</td>
            {doubleTimeRow.map((hours, i) => (
              <td key={i} style={cellStyle}>
                {hours}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
};

const getProjectField = (data, snakeKey, camelKey) =>
  data[camelKey] || data[snakeKey];

const ProjectHoursBreakdown = ({ allocations, hoursType, workPeriodInitialWeekday }) => {
  const codes = Object.keys(allocations);

  const allDetails = [];
  codes.forEach((code) => {
    const data = allocations[code];
    if (hoursType === "standard") {
      allDetails.push(getProjectField(data, "standard_hours_details", "standardHoursDetails") || []);
    } else {
      allDetails.push(getProjectField(data, "non_standard_hours_details", "nonStandardHoursDetails") || []);
      allDetails.push(getProjectField(data, "double_time_hours_details", "doubleTimeHoursDetails") || []);
    }
  });

  const { headers, dayIndices } = getDayLayout(allDetails, workPeriodInitialWeekday);

  return (
    <table
      className="rounded"
      style={{ textAlign: "center", borderCollapse: "collapse" }}
      data-cy="project-hours-breakdown-popover"
    >
      <tbody>
        <tr>
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <th />
          {headers.map((day) => (
            <th key={day} style={{ padding: "4px 6px" }}>
              {day}
            </th>
          ))}
          <th style={{ padding: "4px 8px" }}>Total</th>
        </tr>
        {codes.map((code) => {
          const data = allocations[code];
          const displayCode = data.code || code;
          if (hoursType === "standard") {
            const row = orderDays(
              getProjectField(data, "standard_hours_details", "standardHoursDetails"),
              dayIndices
            );
            const total = row.reduce((s, v) => s + Number(v || 0), 0);
            return (
              <React.Fragment key={code}>
                <tr>
                  <td colSpan={headers.length + 2} style={sectionHeaderStyle}>
                    {displayCode}
                  </td>
                </tr>
                <tr>
                  <td style={boldCellStyle}>Std.</td>
                  {row.map((hours, i) => (
                    <td key={i} style={cellStyle}>
                      {hours}
                    </td>
                  ))}
                  <td style={boldCellStyle}>{total}</td>
                </tr>
              </React.Fragment>
            );
          }
          const emptyRow = headers.map(() => "0");
          const nsRaw = getProjectField(data, "non_standard_hours_details", "nonStandardHoursDetails");
          const nsRow = nsRaw && nsRaw.length > 0 ? orderDays(nsRaw, dayIndices) : emptyRow;
          const dtRaw = getProjectField(data, "double_time_hours_details", "doubleTimeHoursDetails");
          const dtRow = dtRaw && dtRaw.length > 0 ? orderDays(dtRaw, dayIndices) : emptyRow;
          const nsTotal = nsRow.reduce((s, v) => s + Number(v || 0), 0);
          const dtTotal = dtRow.reduce((s, v) => s + Number(v || 0), 0);
          const hasDt = dtRow.some((v) => Number(v || 0) > 0);
          return (
            <React.Fragment key={code}>
              <tr>
                <td colSpan={headers.length + 2} style={sectionHeaderStyle}>
                  {displayCode}
                </td>
              </tr>
              <tr>
                <td style={boldCellStyle}>1.5x</td>
                {nsRow.map((hours, i) => (
                  <td key={i} style={cellStyle}>
                    {hours || 0}
                  </td>
                ))}
                <td style={boldCellStyle}>{nsTotal}</td>
              </tr>
              {hasDt && (
                <tr>
                  <td style={boldCellStyle}>2x</td>
                  {dtRow.map((hours, i) => (
                    <td key={i} style={cellStyle}>
                      {hours || 0}
                    </td>
                  ))}
                  <td style={boldCellStyle}>{dtTotal}</td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const ProjectBillingBreakdown = ({ allocations }) => {
  const codes = Object.keys(allocations);
  return (
    <table className="rounded" style={{ textAlign: "center" }}>
      <tbody>
        <tr>
          <th style={{ padding: "8px" }}>Allocation</th>
          <th style={{ padding: "8px" }}>Additions</th>
        </tr>
        {codes.map((code) => {
          const data = allocations[code];
          const displayCode = data.code || code;
          const billing = getProjectField(data, "additional_billing", "additionalBilling");
          return (
            <tr key={code}>
              <td style={{ padding: "8px", fontWeight: "bold", color: "#008DAE" }}>
                {displayCode}
              </td>
              <td style={{ padding: "8px" }}>
                {formatter.format(billing || 0)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const getAllocationAmounts = (data, hourlyBillRate) => {
  const amounts = data.amounts || {};
  const rate = parseFloat(hourlyBillRate || 0);

  const storedStd = amounts.standardHoursAmount ?? amounts.standard_hours_amount;
  const storedNonStd = amounts.nonStandardHoursAmount ?? amounts.non_standard_hours_amount;
  const storedAdd = amounts.additionalBilling ?? amounts.additional_billing;

  if (storedStd != null) {
    return {
      stdAmount: parseFloat(storedStd),
      nonStdAmount: parseFloat(storedNonStd ?? 0),
      addBilling: parseFloat(storedAdd ?? 0),
    };
  }

  const stdDetails = getProjectField(data, "standard_hours_details", "standardHoursDetails") || [];
  const otDetails = getProjectField(data, "non_standard_hours_details", "nonStandardHoursDetails") || [];
  const dtDetails = getProjectField(data, "double_time_hours_details", "doubleTimeHoursDetails") || [];
  const stdHours = stdDetails.reduce((s, v) => s + Number(v || 0), 0);
  const otHours = otDetails.reduce((s, v) => s + Number(v || 0), 0);
  const dtHours = dtDetails.reduce((s, v) => s + Number(v || 0), 0);

  return {
    stdAmount: stdHours * rate,
    nonStdAmount: otHours * rate * 1.5 + dtHours * rate * 2,
    addBilling: parseFloat(getProjectField(data, "additional_billing", "additionalBilling") || 0),
  };
};

const AllocationBillBreakdown = ({ allocations, hourlyBillRate }) => {
  const codes = Object.keys(allocations);

  let totalStd = 0;
  let totalNonStd = 0;
  let totalAdd = 0;

  const rows = codes.map((code) => {
    const data = allocations[code];
    const amounts = getAllocationAmounts(data, hourlyBillRate);
    totalStd += amounts.stdAmount;
    totalNonStd += amounts.nonStdAmount;
    totalAdd += amounts.addBilling;
    return { code, displayCode: data.code || code, ...amounts };
  });

  const thStyle = { padding: "8px", textAlign: "right" };
  const tdStyle = { padding: "8px", textAlign: "right" };
  const labelStyle = { padding: "8px", fontWeight: "bold", color: "#008DAE", textAlign: "left" };
  const totalLabelStyle = { ...labelStyle, color: "#37474F", borderTop: "2px solid #e0e0e0" };
  const totalTdStyle = { ...tdStyle, fontWeight: "bold", borderTop: "2px solid #e0e0e0" };

  return (
    <table className="rounded" style={{ textAlign: "center", borderCollapse: "collapse" }}>
      <tbody>
        <tr>
          <th style={{ ...thStyle, textAlign: "left" }}>Allocation</th>
          <th style={thStyle}>Std. Hours</th>
          <th style={thStyle}>Non-Std.</th>
          <th style={thStyle}>Additions</th>
        </tr>
        {rows.map((row) => (
          <tr key={row.code}>
            <td style={labelStyle}>{row.displayCode}</td>
            <td style={tdStyle}>{formatter.format(row.stdAmount)}</td>
            <td style={tdStyle}>{formatter.format(row.nonStdAmount)}</td>
            <td style={tdStyle}>{formatter.format(row.addBilling)}</td>
          </tr>
        ))}
        <tr>
          <td style={totalLabelStyle}>Total</td>
          <td style={totalTdStyle}>{formatter.format(totalStd)}</td>
          <td style={totalTdStyle}>{formatter.format(totalNonStd)}</td>
          <td style={totalTdStyle}>{formatter.format(totalAdd)}</td>
        </tr>
      </tbody>
    </table>
  );
};

function CustomPayRates({ dataItems }) {
  return (
    <table className="rounded" style={{ textAlign: "center" }}>
      <tbody>
        <tr>
          <th>Pay Rule</th>
          <th>Hours</th>
          <th>Bill Amount</th>
        </tr>
        {map(dataItems, ({ name, hours, wages }) => (
          <tr key={name}>
            <td style={{ padding: "8px" }}>{name}</td>
            <td style={{ padding: "8px" }}>{parseFloat(hours).toFixed(2)}</td>
            <td style={{ padding: "8px" }}>${parseFloat(wages).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export const StandardHours = (props) => {
  const sharedClasses = useSharedStyles();
  const {
    standardHours,
    standardHoursDetails,
    customPayRatesData,
    allocations,
    workPeriodInitialWeekday,
  } = props;

  if (parseFloat(standardHours || 0) <= 0) {
    return <span>0</span>;
  }

  const hasProjectBreakdown =
    hasAllocations(allocations) &&
    Object.keys(allocations).length > 1;

  if (!standardHoursDetails && isEmpty(customPayRatesData) && !hasProjectBreakdown) {
    return <span>{parseFloat(standardHours || 0)}</span>;
  }

  const cellContent = (handleClick) => (
    <span
      className={sharedClasses.linkText}
      onMouseEnter={handleClick}
      onMouseLeave={handleClick}
    >
      {parseFloat(standardHours || 0.0)}
    </span>
  );

  const renderPopoverContent = () => {
    if (hasProjectBreakdown) {
      return (
        <ProjectHoursBreakdown
          allocations={allocations}
          hoursType="standard"
          workPeriodInitialWeekday={workPeriodInitialWeekday}
        />
      );
    }
    if (!isEmpty(customPayRatesData)) {
      return <CustomPayRates dataItems={customPayRatesData} />;
    }
    return popoverTable(props);
  };

  return (
    <PopoverLink
      renderLink={cellContent}
      renderPopoverContent={renderPopoverContent}
    />
  );
};

export const NonStandardHours = (props) => {
  const sharedClasses = useSharedStyles();
  const {
    nonStandardHours,
    doubleTimeHours,
    nonStandardHoursDetails,
    doubleTimeHoursDetails,
    customPayRatesData,
    allocations,
    workPeriodInitialWeekday,
  } = props;

  const totalHours =
    parseFloat(nonStandardHours || 0.0) + parseFloat(doubleTimeHours || 0.0);

  if (!isEmpty(customPayRatesData)) {
    return "See std.";
  }

  if (totalHours <= 0) {
    return <span>0</span>;
  }

  const hasProjectBreakdown =
    hasAllocations(allocations) &&
    Object.keys(allocations).length > 1;

  const renderLink = (handleClick) => (
    <span
      className={`${sharedClasses.linkText}`}
      onMouseEnter={handleClick}
      onMouseLeave={handleClick}
    >
      {totalHours}
    </span>
  );

  const oldPopoverContent = () => (
    <table className="rounded" style={{ textAlign: "center" }}>
      <tbody>
        <tr>
          <th>Rate</th>
          <th>Hours</th>
        </tr>
        <tr>
          <td style={cellStyle}>1.5x</td>
          <td style={cellStyle}>{parseFloat(nonStandardHours || 0.0)}</td>
        </tr>
        <tr>
          <td style={cellStyle}>2x</td>
          <td style={cellStyle}>{parseFloat(doubleTimeHours || 0.0)}</td>
        </tr>
      </tbody>
    </table>
  );

  const renderPopoverContent = () => {
    if (hasProjectBreakdown) {
      return (
        <ProjectHoursBreakdown
          allocations={allocations}
          hoursType="nonStandard"
          workPeriodInitialWeekday={workPeriodInitialWeekday}
        />
      );
    }
    if (nonStandardHoursDetails || doubleTimeHoursDetails) {
      return popoverTable(props);
    }
    return oldPopoverContent();
  };

  return (
    <PopoverLink
      renderLink={renderLink}
      renderPopoverContent={renderPopoverContent}
    />
  );
};

export const AdditionalBilling = ({
  additionalBilling,
  additionalBillingReason,
  allocations,
}) => {
  const sharedClasses = useSharedStyles();

  const hasProjectBreakdown =
    hasAllocations(allocations) &&
    Object.keys(allocations).length > 1;

  const hasPopover = additionalBilling > 0 || hasProjectBreakdown;

  const renderLink = (handleClick) => (
    <span
      className={`${hasPopover ? sharedClasses.linkText : ""}`}
      onMouseEnter={handleClick}
      onMouseLeave={handleClick}
    >
      {formatter.format(additionalBilling)}
    </span>
  );

  const renderPopoverContent = () => {
    if (hasProjectBreakdown) {
      return <ProjectBillingBreakdown allocations={allocations} />;
    }
    return (
      <table className="rounded" style={{ margin: 8, textAlign: "center" }}>
        <tbody>
          <tr style={{ whiteSpace: "pre" }}>{additionalBillingReason}</tr>
        </tbody>
      </table>
    );
  };

  if (hasPopover) {
    return (
      <PopoverLink
        renderLink={renderLink}
        renderPopoverContent={renderPopoverContent}
      />
    );
  }

  return <span>{formatter.format(additionalBilling)}</span>;
};

export const CombinedHours = ({
  standardHours,
  nonStandardHours,
  doubleTimeHours,
  standardHoursDetails,
  nonStandardHoursDetails,
  doubleTimeHoursDetails,
  customPayRatesData,
  workPeriodInitialWeekday,
}) => {
  const totalHours =
    parseFloat(standardHours || 0.0) +
    parseFloat(nonStandardHours || 0.0) +
    parseFloat(doubleTimeHours || 0.0);
  const sharedClasses = useSharedStyles();

  const renderLink = (handleClick) => (
    <span
      className={`${sharedClasses.linkText}`}
      onMouseEnter={handleClick}
      onMouseLeave={handleClick}
    >
      {totalHours}
    </span>
  );

  const popoverContent = () => {
    if (!isEmpty(customPayRatesData)) {
      return <CustomPayRates dataItems={customPayRatesData} />;
    }

    if (
      standardHoursDetails ||
      nonStandardHoursDetails ||
      doubleTimeHoursDetails
    ) {
      return popoverTable({
        standardHoursDetails,
        nonStandardHoursDetails,
        doubleTimeHoursDetails,
        workPeriodInitialWeekday,
      });
    }

    return (
      <table className="rounded" style={{ textAlign: "center" }}>
        <tbody>
          <tr>
            <th>Rate</th>
            <th>Hours</th>
          </tr>
          {standardHours && (
            <tr>
              <td style={{ padding: "8px" }}>Std. Hours</td>
              <td style={{ padding: "8px" }}>{standardHours}</td>
            </tr>
          )}
          {nonStandardHours && (
            <tr>
              <td style={{ padding: "8px" }}>Non-Std. Hours</td>
              <td style={{ padding: "8px" }}>{nonStandardHours}</td>
            </tr>
          )}
          {doubleTimeHours && (
            <tr>
              <td style={{ padding: "8px" }}>Double Time Hours</td>
              <td style={{ padding: "8px" }}>{doubleTimeHours}</td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  return (
    <PopoverLink
      renderLink={renderLink}
      renderPopoverContent={popoverContent}
    />
  );
};

const BillAmount = (props) => {
  const { totalAmount, disputedAt, documentUrl, allocations, hourlyBillRate } = props;
  const sharedClasses = useSharedStyles();

  const hasProjectBreakdown =
    hasAllocations(allocations) &&
    Object.keys(allocations).length > 1 &&
    !disputedAt;

  const billContent = (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <span className="bold" style={disputedAt ? { color: "#FF6D6D" } : {}}>
        {disputedAt ? "Disputed By Approving Party" : totalAmount}
      </span>
      {documentUrl && (
        <a
          href={documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", alignItems: "center", color: "#008DAE" }}
        >
          <AttachFileIcon style={{ fontSize: "18px" }} />
        </a>
      )}
    </div>
  );

  if (!hasProjectBreakdown) {
    return billContent;
  }

  const renderLink = (handleClick) => (
    <div
      style={{ display: "flex", alignItems: "center", gap: "6px" }}
      onMouseEnter={handleClick}
      onMouseLeave={handleClick}
    >
      <span className={`bold ${sharedClasses.linkText}`}>{totalAmount}</span>
      {documentUrl && (
        <a
          href={documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", alignItems: "center", color: "#008DAE" }}
        >
          <AttachFileIcon style={{ fontSize: "18px" }} />
        </a>
      )}
    </div>
  );

  return (
    <PopoverLink
      renderLink={renderLink}
      renderPopoverContent={() => (
        <AllocationBillBreakdown allocations={allocations} hourlyBillRate={hourlyBillRate} />
      )}
    />
  );
};

const tableColumns = [
  {
    colKey: "AVATAR",
    label: "",
    renderData: ({ avatarIcon, firstName, lastName }) => (
      <GlobalAvatar
        individual={{
          avatarIcon,
          firstName,
          lastName,
        }}
      />
    ),
    style: { width: "6%" },
  },
  {
    colKey: "NAME",
    label: <span className="overline-heading">Worker</span>,
    renderData: ({ recruiterSubmissionId, employer, recruiter }) => (
      <RecruiterSubmissionName
        {...{ recruiterSubmissionId, employer, recruiter }}
      />
    ),
    style: { width: "16%" },
    orderFunc: ({ lastName }) => lastName.toLowerCase(),
  },
  {
    colKey: "TITLE",
    label: <span className="overline-heading">Job</span>,
    renderData: ({ jobTitle }) => (
      <span
        style={{
          whiteSpace: "normal",
          overflowWrap: "break-word",
          paddingRight: "10px",
        }}
      >
        {jobTitle}
      </span>
    ),
    style: { width: "20%" },
    orderFunc: ({ jobTitle }) => jobTitle.toLowerCase(),
  },
  {
    colKey: "STANDARD_HOURS",
    label: <span className="overline-heading">Std. Hours</span>,
    renderData: StandardHours,
    style: { width: "8%" },
    orderFunc: ({ standardHours }) => parseFloat(standardHours || 0),
  },
  {
    colKey: "NON_STANDARD_HOURS",
    label: <span className="overline-heading">Non-Std. Hours</span>,
    renderData: NonStandardHours,
    style: { width: "10%" },
    // eslint-disable-next-line max-len
    orderFunc: ({ nonStandardHours, doubleTimeHours }) =>
      parseFloat(nonStandardHours || 0.0) + parseFloat(doubleTimeHours || 0.0),
  },
  {
    colKey: "ADDITIONAL_BILLING",
    label: <span className="overline-heading">Additions</span>,
    renderData: AdditionalBilling,
    style: { width: "10%" },
    orderFunc: ({ additionalBilling }) => additionalBilling,
  },
  {
    colKey: "WEEKLY_BILL",
    label: <span className="overline-heading">Bill Amount</span>,
    renderData: BillAmount,
    style: { width: "12%" },
    orderFunc: ({ totalAmount }) => totalAmount,
  },
];

export { AllocationBillBreakdown };
export default tableColumns;
